import { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left.svg';
import {
  FIRST_HELP_QUESTION_ID,
  HELP_QUESTIONS,
  type HelpQuestionOptionType,
} from './questions';

type PropsType = {
  onBack: () => void;
  onComplete?: (skipTraceType: string) => void;
};

const SkipTraceHelpWizard = ({ onBack, onComplete }: PropsType) => {
  const theme = useTheme();
  const [currentQuestionId, setCurrentQuestionId] = useState(FIRST_HELP_QUESTION_ID);
  const [questionHistory, setQuestionHistory] = useState<string[]>([]);

  const currentQuestion = HELP_QUESTIONS[currentQuestionId];
  const canGoBackAStep = questionHistory.length > 0;

  const handleSelectOption = (option: HelpQuestionOptionType) => {
    if (option.nextQuestionId && HELP_QUESTIONS[option.nextQuestionId]) {
      setQuestionHistory((prev) => [...prev, currentQuestionId]);
      setCurrentQuestionId(option.nextQuestionId);
    }
  };

  const handleStepBack = () => {
    if (!canGoBackAStep) {
      onBack();
      return;
    }

    const previousQuestionId = questionHistory[questionHistory.length - 1];

    setQuestionHistory((prev) => prev.slice(0, -1));
    setCurrentQuestionId(previousQuestionId);
  };

  const handleContinueToUpload = () => {
    if (currentQuestion?.skipTraceType) {
      onComplete?.(currentQuestion.skipTraceType);
    }
  };

  if (!currentQuestion) {
    return null;
  }

  let questionContent = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="h5" fontWeight={600} textAlign="center">
          {currentQuestion.text}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection:
              currentQuestion.options.length > 2
                ? 'column'
                : { xs: 'column', sm: 'row' },
            gap: 2,
            width: '100%',
            maxWidth: currentQuestion.options.length > 2 ? 480 : 420,
          }}
        >
          {currentQuestion.options.map((option) => (
            <Button
              key={option.id}
              variant="outlined"
              onClick={() => handleSelectOption(option)}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                whiteSpace: 'pre-line',
                lineHeight: 1.4,
              }}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      </Box>

      {currentQuestion.exampleImage && (
        <Box
          sx={{
            flex: { xs: 'none', md: 1 },
            width: '100%',
            maxWidth: { xs: '100%', md: 520 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Example
          </Typography>
          <Box
            component="img"
            src={currentQuestion.exampleImage}
            alt={currentQuestion.exampleImageAlt || 'Example'}
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 1,
            }}
          />
        </Box>
      )}
    </Box>
  );

  if (currentQuestion.isResult) {
    questionContent = (
      <>
        <Typography variant="h5" fontWeight={600} textAlign="center">
          {currentQuestion.text}
        </Typography>
        <Typography
          variant="h4"
          fontWeight={700}
          textAlign="center"
          color="primary.main"
        >
          {currentQuestion.recommendedTypeLabel}
        </Typography>
        <Button
          variant="contained"
          onClick={handleContinueToUpload}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Continue to upload
        </Button>
      </>
    );
  } else if (currentQuestion.isMessage) {
    questionContent = (
      <>
        <Typography
          variant="h5"
          fontWeight={600}
          textAlign="center"
          maxWidth={520}
        >
          {currentQuestion.text}
        </Typography>
        <Button
          variant="contained"
          onClick={onBack}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Back to upload
        </Button>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 2,
      }}
    >
      <Box>
        <Button
          startIcon={<ArrowLeftIcon fill={theme.palette.primary.main} />}
          onClick={handleStepBack}
          sx={{
            px: 2,
            borderRadius: 2,
            textTransform: 'capitalize',
          }}
        >
          {canGoBackAStep ? 'Back' : 'Back to upload'}
        </Button>
      </Box>

      <Box
        sx={{
          width: '100%',
          py: 5,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          minHeight: 280,
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {questionContent}
      </Box>
    </Box>
  );
};

export default SkipTraceHelpWizard;
